/**
 * REDEX Communications Portal
 * 
 * PURPOSE: "Close deals without leaving the system"
 * 
 * - Send emails via Amazon SES
 * - Send SMS via Twilio (when configured)
 * - All communications logged automatically
 * - Tied to leads/opportunities
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  Tabs,
  Tab,
  Badge,
  Autocomplete,
  useTheme,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Email as EmailIcon,
  Sms as SmsIcon,
  Phone as PhoneIcon,
  Send as SendIcon,
  Person as PersonIcon,
  History as HistoryIcon,
  Add as AddIcon,
  ContentCopy as TemplateIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { leadsService, Lead } from '../services/leadsService';
import { useNavigate } from 'react-router-dom';

// Types
interface Contact {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  company?: string | null;
  type: 'lead' | 'client' | 'contact';
  lastContacted?: Date;
}

interface CommunicationLog {
  id: string;
  contactId: string;
  contactName: string;
  type: 'email' | 'sms' | 'call';
  subject?: string;
  content: string;
  status: 'sent' | 'delivered' | 'failed' | 'pending';
  timestamp: Date;
}

interface EmailTemplate {
  id: string;
  name: string;
  subject: string;
  body: string;
  category: 'outreach' | 'follow-up' | 'proposal' | 'general';
}

// Email templates
const defaultTemplates: EmailTemplate[] = [
  {
    id: '1',
    name: 'Initial Outreach',
    subject: 'Quick question about {{company}}',
    body: `Hi {{name}},

I noticed {{company}} and wanted to reach out about how we could help with your digital presence.

Would you have 15 minutes this week for a quick call?

Best,
[Your Name]`,
    category: 'outreach',
  },
  {
    id: '2',
    name: 'Follow-Up',
    subject: 'Following up on our conversation',
    body: `Hi {{name}},

Just wanted to follow up on our previous conversation. Have you had a chance to consider our proposal?

I'm happy to answer any questions you might have.

Best,
[Your Name]`,
    category: 'follow-up',
  },
  {
    id: '3',
    name: 'Proposal',
    subject: 'Proposal for {{company}} - SEO & Lead Generation',
    body: `Hi {{name}},

Thank you for your time today. As discussed, I've put together a proposal for {{company}}.

Key deliverables:
- SEO optimization for your website
- Lead generation campaign
- Monthly reporting and analysis

Let me know if you'd like to schedule a call to discuss further.

Best,
[Your Name]`,
    category: 'proposal',
  },
];

const CommunicationsPortal: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [communicationLogs, setCommunicationLogs] = useState<CommunicationLog[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  
  // Compose state
  const [communicationType, setCommunicationType] = useState<'email' | 'sms'>('email');
  const [emailSubject, setEmailSubject] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  
  // Dialog state
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  
  // Feedback
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });
  
  // Load data
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    setLoading(true);
    try {
      // Load leads as contacts
      const leadsResponse = await leadsService.getLeads({ limit: 100 });
      const contactsList: Contact[] = leadsResponse.leads.map((lead: Lead) => ({
        id: lead.id,
        name: lead.name,
        email: lead.email,
        phone: lead.phone,
        company: lead.company,
        type: 'lead' as const,
        lastContacted: lead.contacted_at ? new Date(lead.contacted_at) : undefined,
      }));
      setContacts(contactsList);
      
      // Load communication logs from email_logs table
      try {
        const logsResponse = await api.get('/emails?limit=50&sortOrder=desc');
        if (logsResponse.data.success && logsResponse.data.data) {
          const emailData = logsResponse.data.data.data || logsResponse.data.data;
          const logs: CommunicationLog[] = (Array.isArray(emailData) ? emailData : []).map((log: any) => ({
            id: log.id,
            contactId: log.recipient_id || '',
            contactName: log.recipient_name || log.to_email || 'Unknown',
            type: 'email' as const,
            subject: log.subject,
            content: log.body || '',
            status: log.status || 'sent',
            timestamp: new Date(log.sent_at || log.created_at),
          }));
          setCommunicationLogs(logs);
        }
      } catch (e) {
        // Logs might not exist yet
        console.warn('Could not load email logs:', e);
      }
      
    } catch (error) {
      console.error('Failed to load data:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load contacts',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Apply template
  const applyTemplate = (template: EmailTemplate) => {
    let subject = template.subject;
    let body = template.body;
    
    // Replace placeholders if contact selected
    if (selectedContact) {
      subject = subject.replace(/\{\{name\}\}/g, selectedContact.name);
      subject = subject.replace(/\{\{company\}\}/g, selectedContact.company || 'your company');
      body = body.replace(/\{\{name\}\}/g, selectedContact.name);
      body = body.replace(/\{\{company\}\}/g, selectedContact.company || 'your company');
    }
    
    setEmailSubject(subject);
    setMessageBody(body);
    setSelectedTemplate(template);
    setTemplateDialogOpen(false);
  };
  
  // Send communication
  const handleSend = async () => {
    if (!selectedContact) {
      setSnackbar({
        open: true,
        message: 'Please select a contact',
        severity: 'error',
      });
      return;
    }
    
    if (!messageBody.trim()) {
      setSnackbar({
        open: true,
        message: 'Please enter a message',
        severity: 'error',
      });
      return;
    }
    
    setSending(true);
    try {
      if (communicationType === 'email') {
        // Send email via API - single recipient
        const response = await api.post('/emails/send', {
          to: selectedContact.email,
          subject: emailSubject,
          htmlBody: messageBody.replace(/\n/g, '<br>'),
          textBody: messageBody,
        });
        
        if (response.data.success) {
          // Add to logs
          const newLog: CommunicationLog = {
            id: response.data.data?.messageId || Date.now().toString(),
            contactId: selectedContact.id,
            contactName: selectedContact.name,
            type: 'email',
            subject: emailSubject,
            content: messageBody,
            status: 'sent',
            timestamp: new Date(),
          };
          setCommunicationLogs([newLog, ...communicationLogs]);
          
          // Update lead status
          try {
            await leadsService.updateLead(selectedContact.id, {
              status: 'contacted',
            });
          } catch (e) {
            // Lead update is best-effort
          }
          
          setSnackbar({
            open: true,
            message: `Email sent to ${selectedContact.name}`,
            severity: 'success',
          });
          
          // Clear form
          setMessageBody('');
          setEmailSubject('');
          setSelectedContact(null);
          setSelectedTemplate(null);
        } else {
          throw new Error(response.data.error || 'Failed to send email');
        }
      } else {
        // SMS not yet implemented
        setSnackbar({
          open: true,
          message: 'SMS feature coming soon. Configure Twilio to enable.',
          severity: 'info',
        });
      }
    } catch (error: any) {
      console.error('Failed to send:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to send message',
        severity: 'error',
      });
    } finally {
      setSending(false);
    }
  };
  
  // Tab panels
  const ComposePanel = () => (
    <Grid container spacing={3}>
      {/* Contact Selection */}
      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 2, height: '100%' }}>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Select Contact
          </Typography>
          
          <Autocomplete
            options={contacts}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            value={selectedContact}
            onChange={(_, newValue) => setSelectedContact(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Search contacts..."
                variant="outlined"
                size="small"
              />
            )}
            renderOption={(props, option) => (
              <li {...props}>
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.company ? `${option.company} • ` : ''}{option.email}
                  </Typography>
                </Box>
              </li>
            )}
            sx={{ mb: 2 }}
          />
          
          {selectedContact && (
            <Card variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  {selectedContact.name.charAt(0)}
                </Avatar>
                <Box>
                  <Typography fontWeight={600}>{selectedContact.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedContact.company}
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2">
                <EmailIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                {selectedContact.email}
              </Typography>
              {selectedContact.phone && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  <PhoneIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                  {selectedContact.phone}
                </Typography>
              )}
              <Chip
                label={selectedContact.type.toUpperCase()}
                size="small"
                color={selectedContact.type === 'lead' ? 'warning' : 'success'}
                sx={{ mt: 2 }}
              />
            </Card>
          )}
          
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => navigate('/leads')}
            sx={{ mt: 2 }}
          >
            Add New Contact
          </Button>
        </Paper>
      </Grid>
      
      {/* Message Composer */}
      <Grid item xs={12} md={8}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Compose Message
            </Typography>
            <ToggleButtonGroup
              value={communicationType}
              exclusive
              onChange={(_, val) => val && setCommunicationType(val)}
              size="small"
            >
              <ToggleButton value="email">
                <EmailIcon sx={{ mr: 1 }} /> Email
              </ToggleButton>
              <ToggleButton value="sms">
                <SmsIcon sx={{ mr: 1 }} /> SMS
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          
          {communicationType === 'email' && (
            <>
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                    }
                  }}
                  size="small"
                />
                <Button
                  variant="outlined"
                  startIcon={<TemplateIcon />}
                  onClick={() => setTemplateDialogOpen(true)}
                >
                  Templates
                </Button>
              </Box>
            </>
          )}
          
          <TextField
            fullWidth
            multiline
            rows={12}
            label={communicationType === 'email' ? 'Message Body' : 'SMS Message'}
            value={messageBody}
            onChange={(e) => setMessageBody(e.target.value)}
            placeholder={communicationType === 'email' 
              ? 'Write your email message here...'
              : 'Write your SMS message (160 chars recommended)...'
            }
            sx={{ mb: 2 }}
          />
          
          {selectedTemplate && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Using template: <strong>{selectedTemplate.name}</strong>
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setMessageBody('');
                setEmailSubject('');
                setSelectedTemplate(null);
              }}
            >
              Clear
            </Button>
            <Button
              variant="contained"
              size="large"
              startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
              onClick={handleSend}
              disabled={sending || !selectedContact}
            >
              {sending ? 'Sending...' : 'Send'}
            </Button>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
  
  const HistoryPanel = () => (
    <Paper>
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" fontWeight={600}>
          <HistoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Communication History
        </Typography>
      </Box>
      <List>
        {communicationLogs.length === 0 ? (
          <ListItem>
            <ListItemText
              primary="No communications yet"
              secondary="Send your first message to see history here"
            />
          </ListItem>
        ) : (
          communicationLogs.map((log, index) => (
            <React.Fragment key={log.id}>
              <ListItem>
                <ListItemAvatar>
                  <Avatar sx={{ 
                    bgcolor: log.type === 'email' 
                      ? alpha(theme.palette.primary.main, 0.1) 
                      : alpha(theme.palette.success.main, 0.1) 
                  }}>
                    {log.type === 'email' ? (
                      <EmailIcon color="primary" />
                    ) : log.type === 'sms' ? (
                      <SmsIcon color="success" />
                    ) : (
                      <PhoneIcon color="info" />
                    )}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight={600}>{log.contactName}</Typography>
                      <Chip
                        label={log.status}
                        size="small"
                        color={log.status === 'sent' || log.status === 'delivered' ? 'success' : 'error'}
                        sx={{ height: 20, fontSize: '0.65rem' }}
                      />
                    </Box>
                  }
                  secondary={
                    <>
                      {log.subject && (
                        <Typography variant="body2" color="text.secondary">
                          {log.subject}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.disabled">
                        {new Date(log.timestamp).toLocaleString()}
                      </Typography>
                    </>
                  }
                />
              </ListItem>
              {index < communicationLogs.length - 1 && <Divider />}
            </React.Fragment>
          ))
        )}
      </List>
    </Paper>
  );
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }
  
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
      <Container maxWidth="xl">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            <EmailIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
            Communications Portal
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Reach out to leads and clients directly. All messages logged automatically.
          </Typography>
        </Box>
        
        {/* Stats */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="primary.main">
                {contacts.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Contacts
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="success.main">
                {communicationLogs.filter(l => l.status === 'sent').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Messages Sent
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="info.main">
                {contacts.filter(c => c.type === 'lead').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Leads to Contact
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} md={3}>
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="h4" fontWeight={700} color="warning.main">
                {defaultTemplates.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Email Templates
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        
        {/* Tabs */}
        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab icon={<SendIcon />} iconPosition="start" label="Compose" />
            <Tab 
              icon={
                <Badge badgeContent={communicationLogs.length} color="primary">
                  <HistoryIcon />
                </Badge>
              } 
              iconPosition="start" 
              label="History" 
            />
          </Tabs>
        </Paper>
        
        {/* Content */}
        {activeTab === 0 && <ComposePanel />}
        {activeTab === 1 && <HistoryPanel />}
        
        {/* Template Dialog */}
        <Dialog
          open={templateDialogOpen}
          onClose={() => setTemplateDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <TemplateIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Email Templates
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2}>
              {defaultTemplates.map((template) => (
                <Grid item xs={12} md={6} key={template.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        bgcolor: alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                    onClick={() => applyTemplate(template)}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography fontWeight={600}>{template.name}</Typography>
                        <Chip label={template.category} size="small" />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Subject: {template.subject}
                      </Typography>
                      <Typography variant="caption" color="text.disabled" sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}>
                        {template.body}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
          </DialogActions>
        </Dialog>
        
        {/* Snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            severity={snackbar.severity}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
};

export default CommunicationsPortal;
